/**
 * Título dos cards gerado da configuração: diz o que o passo faz com o
 * pedido. As linhas de resumo embaixo dele (quando, para quem, com que
 * limite) ficam em `node-summary.ts`. Puro: o card e o painel passam os
 * nomes que já buscaram.
 */

import type {
  DispatchConfig,
  DispatchTiming,
  OfferConfig,
  PromisedDateConfig,
  Weekday,
  WorkflowNode,
} from '../types';
import type { MethodRef, WorkflowCatalog } from '../api/types';
import { kindLabel } from './catalog';

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** "45 min", "2 h", "1 h 30 min", "3 d". */
export function formatMinutes(m: number): string {
  if (!Number.isFinite(m) || m < 1) return '0 min';
  if (m < 60) return `${m} min`;
  if (m % 60 !== 0) return `${Math.floor(m / 60)} h ${m % 60} min`;
  return `${m / 60} h`;
}

export type TimeUnit = 'min' | 'h';

/**
 * Minutos gravados → número e unidade do campo: hora cheia em horas, o
 * resto em minutos (0 fica em minutos). A unidade é só de exibição.
 */
export function splitMinutes(minutes: number): {
  value: number;
  unit: TimeUnit;
} {
  return minutes > 0 && minutes % 60 === 0
    ? { value: minutes / 60, unit: 'h' }
    : { value: minutes, unit: 'min' };
}

/** Minutos de um valor na unidade do campo. */
export const toMinutes = (value: number, unit: TimeUnit): number =>
  unit === 'h' ? value * 60 : value;

/** Metros → "5 km" / "2,5 km". */
export function formatKm(meters: number): string {
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(
    meters / 1000,
  )} km`;
}

/* ----- Envio ----- */

export const DISPATCH_ACTION_TITLE: Record<DispatchConfig['action'], string> = {
  SPECIFIC_METHOD: 'Prazo específico',
  CHEAPEST_METHOD: 'Mais barato',
  FASTEST_METHOD: 'Mais rápido',
  LAST_METHOD: 'Repetir o último prazo',
};

/** Momento do envio no chip do card. */
export const DISPATCH_TIMING_CHIP: Record<DispatchTiming, string> = {
  IMMEDIATE: 'agora',
  NEXT_AVAILABLE: 'agora ou na abertura da filial',
  NEXT_OPENING: 'na abertura da filial',
  DELIVERY_WINDOW_START: 'no início da janela',
  WEEKLY: 'horário fixo por dia',
};

export const WEEKDAYS: Array<{ id: Weekday; short: string }> = [
  { id: 'mon', short: 'seg' },
  { id: 'tue', short: 'ter' },
  { id: 'wed', short: 'qua' },
  { id: 'thu', short: 'qui' },
  { id: 'fri', short: 'sex' },
  { id: 'sat', short: 'sáb' },
  { id: 'sun', short: 'dom' },
];

/**
 * Id do prazo encurtado para quando não há nome nenhum: grupo cortado e o
 * tipo ("bbc21b95… · D1"). Honesto: mostra que há prazo, sem inventar nome.
 */
export function methodShortId(methodId: string): string {
  const cut = methodId.lastIndexOf(':');
  const group = cut < 0 ? methodId : methodId.slice(0, cut);
  const type = cut < 0 ? '' : methodId.slice(cut + 1);
  const shortGroup = group.length > 12 ? `${group.slice(0, 8)}…` : group;
  return type ? `${shortGroup} · ${type}` : shortGroup;
}

/**
 * O que o Envio faz, gerado da escolha ("LALAMOVE · CAR EXP30", "Mais
 * barato"). Prazo específico: a cópia gravada no passo; sem ela (grafo
 * montado fora do editor), o prazo resolvido pelo `/methods` (`method`);
 * sem nome nenhum, o id curto. "Escolha um prazo" só sem prazo (DN-3).
 */
export function dispatchChoice(
  cfg: DispatchConfig,
  method?: Pick<
    MethodRef,
    'carrier_name' | 'method_group_name' | 'method_type'
  >,
): string {
  if (cfg.action === 'SPECIFIC_METHOD') {
    if (cfg.carrier && cfg.modal) return `${cfg.carrier} · ${cfg.modal}`;
    if (!cfg.methodId) return 'Escolha um prazo';
    if (method?.carrier_name) {
      return [method.carrier_name, method.method_group_name, method.method_type]
        .filter(Boolean)
        .join(' · ');
    }
    return `Prazo ${methodShortId(cfg.methodId)}`;
  }
  return DISPATCH_ACTION_TITLE[cfg.action] ?? 'Envio';
}

/** Título do card: o apelido, se houver; senão a escolha. */
export function dispatchTitle(
  cfg: DispatchConfig,
  method?: Parameters<typeof dispatchChoice>[1],
): string {
  return cfg.title?.trim() || dispatchChoice(cfg, method);
}

/* ----- Ofertar a motoristas ----- */

export function offerTitle(
  cfg: OfferConfig,
  tagName?: (id: string) => string | undefined,
): string {
  if (cfg.title?.trim()) return cfg.title.trim();
  if (cfg.audience === 'SPECIFIC') {
    return cfg.driverIds.length
      ? plural(cfg.driverIds.length, 'motorista', 'motoristas')
      : 'Escolha os motoristas';
  }
  if (cfg.audience === 'GROUP') {
    if (cfg.driverTagIds.length === 1) {
      const name = tagName?.(cfg.driverTagIds[0]);
      return name ? `Grupo ${name}` : '1 grupo';
    }
    return cfg.driverTagIds.length
      ? plural(cfg.driverTagIds.length, 'grupo', 'grupos')
      : 'Escolha os grupos';
  }
  return 'Todos os motoristas da filial';
}

/* ----- Prazo prometido ----- */

export function promisedDateTitle(cfg: PromisedDateConfig): string {
  return `Criação + ${formatMinutes(cfg.minutesAfterCreated)}`;
}

/** Chip do card: o que acontece com o pedido que já tem prazo. */
export function promisedDateModeChip(cfg: PromisedDateConfig): string {
  return cfg.overwrite
    ? 'substitui o prazo do pedido'
    : 'só se o pedido não tiver prazo';
}

/* ----- Nome curto de qualquer passo (selects, listas) ----- */

export function stepName(node: WorkflowNode, catalog: WorkflowCatalog): string {
  const title = (node.config as { title?: string }).title?.trim();
  if (title) return title;
  if (node.kind === 'offer') return offerTitle(node.config as OfferConfig);
  if (node.kind === 'disp') return dispatchTitle(node.config as DispatchConfig);
  return kindLabel(catalog, node.kind);
}
